import { useEffect, useState } from 'react'
import {
  CarIcon,
  CheckIcon,
  DownloadIcon,
  EyeOffIcon,
  FileBarChartIcon,
  FileSpreadsheetIcon,
  LayoutDashboardIcon,
  Loader2Icon,
  LockIcon,
  RocketIcon,
  SearchIcon,
  UserCogIcon,
  UsersIcon,
} from 'lucide-react'

import { ADVANCED_EXPORTS_ROLE_LABEL, ADVANCED_EXPORT_REPORTS, EXCEL_COLUMN_LETTERS } from '../../data/advancedExports'
import { JourneyStage, JourneyView, useTarget } from '../../journey'

import type { AdvancedExportsState } from './journey'

const RING_CIRCUMFERENCE = 125.6
const GENERATION_LABELS = ['Congelando encabezados', 'Autoajustando columnas', 'Aplicando filtros']

/** Roles as they read in the profile grid; only one is toggled by the journey. */
const PROFILE_PERMISSIONS: { label: string; checked: boolean; isTarget?: boolean }[] = [
  { label: 'Ver vehículos', checked: true },
  { label: 'Ver clientes', checked: true },
  { label: 'Ver reportes del concesionario', checked: true },
  { label: 'Ver reporte de stock', checked: true },
  { label: ADVANCED_EXPORTS_ROLE_LABEL, checked: false, isTarget: true },
  { label: 'Editar notas de venta', checked: false },
  { label: 'Ver perfiles', checked: true },
  { label: 'Publicar en portales', checked: false },
]

const TEAM = [
  { name: 'Ana Pérez', email: 'ana.perez@automotora.cl', job: 'Vendedor', primary: true },
  { name: 'Luis Rojas', email: 'luis.rojas@automotora.cl', job: 'Gerente', primary: false },
  { name: 'Carla Díaz', email: 'carla.diaz@automotora.cl', job: 'Vendedor', primary: false },
]

interface ViewProps {
  state: AdvancedExportsState
}

export const Simulator = ({ state }: ViewProps) => (
  <JourneyStage
    state={state}
    user={{ name: 'Admin' }}
    nav={[
      { label: 'Inicio', icon: LayoutDashboardIcon },
      { label: 'Reportes', icon: FileBarChartIcon, target: 'navReportes', active: state.navActive === 'reportes' },
      { label: 'Usuarios', icon: UsersIcon, target: 'navUsuarios', active: state.navActive === 'usuarios' },
      { label: 'Vehículos', icon: CarIcon },
    ]}
  >
    <ReportsView state={state} />
    <LoadingView />
    <UsersView />
    <PermissionsView state={state} />
    <AdvancedView state={state} />
    <ExcelView state={state} />
  </JourneyStage>
)

/* ---- Reportes without the permission: the tab is absent ---- */
const ReportsView = ({ state }: ViewProps) => {
  const tabRow = useTarget('tabRow')
  const tabSlot = useTarget('tabSlot')

  return (
    <JourneyView name="reports" className="flex flex-col bg-white p-3">
      <h3 className="mb-1.5 text-[10px] font-bold text-[#1c1917]">Reportes</h3>

      <div ref={tabRow} className="mb-2 flex items-end gap-1 border-b border-stone-200">
        <span ref={tabSlot} className="w-0 self-stretch" />
        <div className="border-b-2 border-[#e5484d] px-2 py-1 text-[8px] font-bold text-[#e5484d]">Stock</div>
        <div className="px-2 py-1 text-[8px] text-[#706d71]">Ventas</div>
        <div className="px-2 py-1 text-[8px] text-[#706d71]">Margen</div>
      </div>

      <div className="flex flex-1 flex-col space-y-2 opacity-40">
        <div className="grid grid-cols-3 gap-1.5">
          {[
            ['Stock Activo', '38'],
            ['Días Promedio', '54'],
            ['Valorizado', '$612M'],
          ].map(([label, value]) => (
            <div key={label} className="rounded border border-[#e4e1e4] bg-[#faf8f8] p-1.5">
              <p className="font-mono text-[6px] uppercase text-[#706d71]">{label}</p>
              <p className="mt-0.5 text-[10px] font-bold text-[#1c1917]">{value}</p>
            </div>
          ))}
        </div>
        <div className="flex h-20 shrink-0 items-end justify-around rounded border border-[#e4e1e4] px-3 pb-2">
          {['35%', '60%', '45%', '72%', '52%'].map((height, index) => (
            <div key={index} className="w-3 rounded-t bg-stone-300" style={{ height }} />
          ))}
        </div>
        <div className="min-h-0 flex-1 overflow-hidden rounded border border-[#e4e1e4]">
          <div className="flex bg-[#f3f2f1] px-1.5 py-1 font-mono text-[5.5px] font-bold uppercase text-[#706d71]">
            <div className="flex-1">Patente</div>
            <div className="flex-1">Vehículo</div>
            <div className="w-[22%]">Días</div>
          </div>
          {[
            ['JWKR·92', 'Toyota RAV4', '41'],
            ['PZBL·45', 'Chevrolet Sail', '76'],
            ['HXTF·11', 'Hyundai Tucson', '18'],
          ].map(([plate, vehicle, days]) => (
            <div key={plate} className="flex border-t border-stone-100 px-1.5 py-1 text-[6px]">
              <div className="flex-1 font-mono">{plate}</div>
              <div className="flex-1">{vehicle}</div>
              <div className="w-[22%] font-mono">{days}</div>
            </div>
          ))}
        </div>
      </div>

      <div
        className="vk-annotation absolute left-[6%] right-[6%] top-[26%] z-20 rounded-lg p-2.5"
        style={{
          opacity: state.annotationVisible ? 1 : 0,
          transform: state.annotationVisible ? 'translateY(0)' : 'translateY(6px)',
        }}
      >
        <span className="vk-annotation-pointer" />
        <div className="flex items-start gap-2">
          <span className="shrink-0 rounded bg-[#e5484d]/10 p-1 text-[#e5484d]">
            <EyeOffIcon className="h-3 w-3" />
          </span>
          <div>
            <p className="text-[8px] font-bold leading-tight text-[#1c1917]">No hay pestaña de Exportación Avanzada</p>
            <p className="mt-0.5 text-[7px] leading-snug text-[#706d71]">
              Sin el permiso <span className="font-mono font-bold text-[#e5484d]">{ADVANCED_EXPORTS_ROLE_LABEL}</span>,
              la pestaña no se muestra: el usuario solo ve Stock, Ventas y Margen.
            </p>
          </div>
        </div>
      </div>
    </JourneyView>
  )
}

/* ---- Navigation skeleton ---- */
const LoadingView = () => (
  <JourneyView name="loading" className="bg-white p-3">
    <div className="mb-2 flex items-center gap-2 border-b border-stone-100 pb-1.5">
      <div className="h-3.5 w-3.5 animate-pulse rounded bg-stone-200" />
      <div className="h-3 w-20 animate-pulse rounded bg-stone-200" />
    </div>
    <div className="space-y-2">
      {[24, 16, 20].map((width, index) => (
        <div key={index} className="flex h-9 items-center gap-2 rounded border border-stone-100 bg-stone-50 p-2">
          <div className="h-6 w-6 animate-pulse rounded bg-stone-200" />
          <div className="flex-1 space-y-1">
            <div className="h-2 animate-pulse rounded bg-stone-200" style={{ width: width * 4 }} />
            <div className="h-2 w-12 animate-pulse rounded bg-stone-100" />
          </div>
        </div>
      ))}
    </div>
  </JourneyView>
)

/* ---- Usuarios ---- */
const UsersView = () => {
  const editUser = useTarget('editUser')

  return (
    <JourneyView name="users" className="bg-white p-3">
      <h3 className="mb-2 text-[10px] font-bold text-[#1c1917]">Usuarios</h3>
      <div className="mb-2 flex items-center gap-1 rounded border border-[#e4e1e4] bg-[#faf8f8] px-1.5 py-1">
        <SearchIcon className="h-2.5 w-2.5 text-[#706d71]" />
        <span className="text-[7px] text-[#a8a29e]">Buscar usuario...</span>
      </div>
      <div className="overflow-hidden rounded border border-[#e4e1e4]">
        <div className="flex bg-[#f3f2f1] px-2 py-1 font-mono text-[6px] font-bold uppercase text-[#706d71]">
          <div className="flex-1">Nombre</div>
          <div className="w-[26%]">Cargo</div>
          <div className="w-[18%] text-right">Acción</div>
        </div>
        {TEAM.map((member) => (
          <div
            key={member.email}
            className={`flex items-center border-t border-stone-100 px-2 py-1.5 ${member.primary ? 'bg-[#faf8f8]' : ''}`}
          >
            <div className="flex flex-1 items-center gap-1.5">
              <div className={`h-4 w-4 shrink-0 rounded-full ${member.primary ? 'bg-stone-300' : 'bg-stone-200'}`} />
              <div className="leading-none">
                <p className="text-[8px] font-bold text-[#1c1917]">{member.name}</p>
                <p className="font-mono text-[6px] text-[#706d71]">{member.email}</p>
              </div>
            </div>
            <div className="w-[26%] text-[7px] text-[#706d71]">{member.job}</div>
            <div className="flex w-[18%] justify-end">
              <span
                ref={member.primary ? editUser : undefined}
                className={`rounded px-1.5 py-0.5 text-[6px] font-bold ${
                  member.primary ? 'bg-[#e5484d] text-white' : 'bg-stone-100 text-[#706d71]'
                }`}
              >
                Editar
              </span>
            </div>
          </div>
        ))}
      </div>
    </JourneyView>
  )
}

/* ---- Editar permisos ---- */
const PermissionsView = ({ state }: ViewProps) => {
  const permissionBox = useTarget('permissionBox')
  const save = useTarget('save')

  return (
    <JourneyView name="permissions" className="flex flex-col bg-white p-3">
      <div className="mb-2 flex items-center gap-1.5">
        <UserCogIcon className="h-3 w-3 text-[#e5484d]" />
        <h3 className="text-[9px] font-bold text-[#1c1917]">Editar usuario · Ana Pérez</h3>
      </div>

      <div className="mb-2 grid grid-cols-3 gap-1.5">
        {[
          ['Email', 'ana.perez@...'],
          ['Teléfono', '+56 9 4417 2280'],
          ['Cargo', 'Vendedor'],
        ].map(([label, value]) => (
          <div key={label} className="rounded border border-[#e4e1e4] bg-[#faf8f8] px-1.5 py-1">
            <p className="font-mono text-[5px] uppercase text-[#706d71]">{label}</p>
            <p className="truncate text-[6px] text-[#1c1917]">{value}</p>
          </div>
        ))}
      </div>

      <p className="mb-1 font-mono text-[6px] font-bold uppercase tracking-wider text-[#706d71]">Permisos del perfil</p>

      <div className="grid flex-1 grid-cols-2 content-start gap-x-2 gap-y-1">
        {PROFILE_PERMISSIONS.map((permission) => {
          const isChecked = permission.isTarget ? state.permissionChecked : permission.checked

          return (
            <div
              key={permission.label}
              ref={permission.isTarget ? permissionBox : undefined}
              className="vk-perm-row flex items-center gap-1.5 rounded px-1 py-0.5"
              data-granted={permission.isTarget && state.permissionChecked}
            >
              <span
                className={`vk-perm-box flex h-2.5 w-2.5 shrink-0 items-center justify-center rounded-sm border ${
                  isChecked ? 'border-[#107c41]' : 'border-stone-300 bg-white'
                }`}
                data-checked={isChecked}
              >
                {isChecked && <CheckIcon className="h-2 w-2 text-white" strokeWidth={3} />}
              </span>
              <span className={`text-[6.5px] text-[#1c1917] ${permission.isTarget ? 'font-bold' : ''}`}>
                {permission.label}
              </span>
            </div>
          )
        })}
      </div>

      <div className="mt-1.5 flex justify-end gap-1.5 border-t border-stone-100 pt-1.5">
        <span className="rounded border border-[#e4e1e4] px-2 py-1 text-[7px] text-[#706d71]">Cancelar</span>
        <span
          ref={save}
          className="flex items-center gap-1 rounded bg-[#107c41] px-2.5 py-1 text-[7px] font-bold text-white"
        >
          {state.saving && <Loader2Icon className="h-2 w-2 animate-spin" />}
          <span>Guardar</span>
        </span>
      </div>

      <div
        className="absolute bottom-9 left-1/2 z-30 flex -translate-x-1/2 items-center gap-1 rounded bg-[#107c41] px-2.5 py-1 text-[7px] font-bold text-white shadow-md transition-all duration-300"
        style={{
          opacity: state.savedToast ? 1 : 0,
          transform: `translate(-50%, ${state.savedToast ? '0' : '0.75rem'})`,
        }}
      >
        <CheckIcon className="h-2.5 w-2.5" />
        <span>Permisos actualizados</span>
      </div>
    </JourneyView>
  )
}

/* ---- Exportación Avanzada ---- */
const AdvancedView = ({ state }: ViewProps) => {
  const download = useTarget('download')
  const report = ADVANCED_EXPORT_REPORTS[state.selectedReport ?? 0]

  return (
    <JourneyView name="advanced" className="flex flex-col bg-white p-3">
      <h3 className="mb-1.5 text-[10px] font-bold text-[#1c1917]">Reportes</h3>

      <div className="mb-2 flex items-end gap-1 border-b border-stone-200">
        <div
          className={`flex items-center gap-1 border-b-2 border-[#107c41] px-2 py-1 text-[8px] font-bold text-[#107c41] ${
            state.tabInserted ? 'vk-tab-arrives' : ''
          }`}
        >
          <FileSpreadsheetIcon className="h-2.5 w-2.5" />
          <span>Exportación Avanzada</span>
        </div>
        <div className="px-2 py-1 text-[8px] text-[#706d71]">Stock</div>
        <div className="px-2 py-1 text-[8px] text-[#706d71]">Ventas</div>
        <div className="px-2 py-1 text-[8px] text-[#706d71]">Margen</div>
      </div>

      <div className="mb-2 flex items-end gap-1.5">
        <div className="flex-1">
          <p className="mb-0.5 font-mono text-[5px] uppercase text-[#706d71]">Organización</p>
          <div className="flex items-center justify-between rounded border border-stone-200 bg-stone-100 px-1.5 py-1">
            <span className="truncate text-[6.5px] text-[#706d71]">Automotora Los Andes</span>
            <LockIcon className="h-2 w-2 text-[#a8a29e]" />
          </div>
        </div>
        {[
          ['Desde', '01-07-2026'],
          ['Hasta', '11-08-2026'],
        ].map(([label, value]) => (
          <div key={label} className="w-[22%]">
            <p className="mb-0.5 font-mono text-[5px] uppercase text-[#706d71]">{label}</p>
            <div className="rounded border border-[#e4e1e4] px-1.5 py-1 font-mono text-[6.5px] text-[#1c1917]">
              {value}
            </div>
          </div>
        ))}
      </div>

      <div className="flex min-h-0 flex-1 gap-2">
        <div className="flex w-[36%] flex-col gap-1">
          <p className="font-mono text-[5px] uppercase text-[#706d71]">Seleccionar tipo</p>
          {ADVANCED_EXPORT_REPORTS.map((item, index) => (
            <ReportCard key={item.id} index={index} selected={state.selectedReport === index} />
          ))}
        </div>

        <div className="flex min-w-0 flex-1 flex-col rounded border border-stone-200">
          <div className="flex items-center justify-between border-b border-stone-100 bg-[#faf8f8] px-1.5 py-1">
            <div className="flex items-center gap-1">
              <SearchIcon className="h-2.5 w-2.5 text-[#706d71]" />
              <span className="text-[7px] font-bold text-[#1c1917]">Vista Previa</span>
              {state.selectedReport !== null && !state.previewLoading && (
                <span className="rounded bg-[#107c41]/10 px-1 py-0.5 font-mono text-[5.5px] font-bold text-[#107c41]">
                  {report.count} Registros
                </span>
              )}
            </div>
            <span
              ref={download}
              className={`flex items-center gap-1 rounded px-1.5 py-1 text-[6px] font-bold transition-all duration-200 ${
                state.selectedReport !== null && !state.previewLoading
                  ? 'bg-[#107c41] text-white shadow-sm'
                  : 'bg-stone-200 text-stone-400'
              }`}
            >
              <DownloadIcon className="h-2 w-2" />
              <span>Descargar Excel (.xlsx)</span>
            </span>
          </div>

          <div className="flex-1 overflow-hidden p-1">
            {state.selectedReport === null ? (
              <div className="flex h-full flex-col items-center justify-center gap-1 rounded border border-dashed border-stone-200 text-[#a8a29e]">
                <RocketIcon className="h-4 w-4" />
                <span className="text-[6px]">Selecciona un reporte a la izquierda</span>
              </div>
            ) : state.previewLoading ? (
              <div className="flex h-full flex-col items-center justify-center gap-1 text-[#a8a29e]">
                <Loader2Icon className="h-3.5 w-3.5 animate-spin text-[#107c41]" />
                <span className="font-mono text-[6px]">Cargando datos...</span>
              </div>
            ) : (
              <div className="vk-preview-fade flex h-full flex-col font-mono text-[5.5px]">
                <div className="flex border-b border-stone-300 bg-[#f3f2f1] font-bold text-[#706d71]">
                  {report.columns.map((column) => (
                    <div key={column} className="flex-1 truncate border-r border-stone-200 px-1 py-0.5">
                      {column}
                    </div>
                  ))}
                </div>
                {report.rows.map((row, rowIndex) => (
                  <div
                    key={rowIndex}
                    className={`flex border-b border-stone-100 ${rowIndex % 2 ? '' : 'bg-[#faf8f8]'}`}
                  >
                    {row.map((cell, cellIndex) => (
                      <div
                        key={cellIndex}
                        className={`flex-1 truncate border-r border-stone-100 px-1 py-[3px] ${
                          report.highlightColumn === cellIndex ? 'font-bold text-emerald-700' : 'text-[#1c1917]'
                        }`}
                      >
                        {cell}
                      </div>
                    ))}
                  </div>
                ))}
                <div className="mt-auto flex items-center justify-between pt-1 text-[5px] text-[#a8a29e]">
                  <span>Mostrando 3 de {report.count} registros</span>
                  {report.extraColumns > 0 && (
                    <span className="font-bold text-[#107c41]">+ {report.extraColumns} columnas más</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </JourneyView>
  )
}

/** Its own component so each card can claim its cursor target by index. */
const ReportCard = ({ index, selected }: { index: number; selected: boolean }) => {
  const ref = useTarget(`card${index}`)
  const item = ADVANCED_EXPORT_REPORTS[index]
  const Icon = item.icon

  return (
    <div
      ref={ref}
      className="vk-rep-card flex items-center gap-1 rounded bg-white px-1.5 py-1"
      data-selected={selected}
    >
      <Icon className="h-2.5 w-2.5 shrink-0 text-[#706d71]" />
      <span className="text-[6.5px] font-medium leading-tight text-[#1c1917]">{item.title}</span>
    </div>
  )
}

/* ---- The generated spreadsheet ---- */
const ExcelView = ({ state }: ViewProps) => {
  const [progress, setProgress] = useState(0)
  const report = ADVANCED_EXPORT_REPORTS[state.selectedReport ?? 0]

  useEffect(() => {
    if (!state.generating) {
      setProgress(0)

      return
    }

    const id = window.setInterval(() => {
      setProgress((current) => (current >= 100 ? 100 : current + 4))
    }, 62)

    return () => window.clearInterval(id)
  }, [state.generating])

  return (
    <JourneyView name="excel" className="bg-white">
      <div className="absolute inset-0 flex flex-col p-3">
        <div className="mb-1.5 flex items-center justify-between border-b border-stone-100 pb-1">
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="shrink-0 rounded bg-[#107c41] p-0.5 text-white">
              <FileSpreadsheetIcon className="h-2.5 w-2.5" />
            </span>
            <span className="truncate font-mono text-[7px] font-bold text-[#1c1917]">
              Reporte-{report.id}-2026-08-11T14-22-05.xlsx
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {['Encabezado congelado', 'Filtros activos'].map((badge) => (
              <span
                key={badge}
                className="rounded border border-[#107c41]/30 bg-[#107c41]/5 px-1 py-0.5 font-mono text-[5px] font-bold uppercase text-[#107c41]"
              >
                {badge}
              </span>
            ))}
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded border border-stone-300 bg-[#f3f2f1] text-[6px]">
          <div className="flex border-b border-stone-300 bg-[#f3f2f1] font-mono font-bold text-[#706d71]">
            <div className="w-4 shrink-0 border-r border-stone-200 py-0.5" />
            {report.columns.map((column, index) => (
              <div key={column} className="flex-1 truncate border-r border-stone-200 px-1 py-0.5">
                {EXCEL_COLUMN_LETTERS[index]}
              </div>
            ))}
          </div>
          <div className="flex flex-1 flex-col overflow-hidden bg-white font-mono">
            <div className="flex border-b border-stone-300 bg-[#107c41] font-bold text-white">
              <div className="w-4 shrink-0 border-r border-stone-200 bg-[#f3f2f1] py-0.5 text-center text-[#706d71]">
                1
              </div>
              {report.columns.map((column) => (
                <div
                  key={column}
                  className="flex-1 truncate border-r border-stone-400 px-1 py-0.5 uppercase tracking-wide"
                >
                  {column}
                  <span className="vk-filter-arrow" />
                </div>
              ))}
            </div>
            {report.rows.map((row, rowIndex) => (
              <div key={rowIndex} className={`flex border-b border-stone-100 ${rowIndex % 2 ? '' : 'bg-[#f9fafb]'}`}>
                <div className="w-4 shrink-0 border-r border-stone-200 bg-[#f3f2f1] py-0.5 text-center text-[#706d71]">
                  {rowIndex + 2}
                </div>
                {row.map((cell, cellIndex) => (
                  <div
                    key={cellIndex}
                    className={`flex-1 truncate border-r border-stone-100 px-1 py-0.5 ${
                      report.highlightColumn === cellIndex ? 'font-bold text-emerald-700' : ''
                    }`}
                  >
                    {cell}
                  </div>
                ))}
              </div>
            ))}
            {/* Empty trailing rows, the way a real sheet keeps going past the data. */}
            {Array.from({ length: 14 }, (_, index) => (
              <div
                key={`blank-${index}`}
                className={`flex border-b border-stone-100 ${index % 2 ? '' : 'bg-[#f9fafb]'}`}
              >
                <div className="w-4 shrink-0 border-r border-stone-200 bg-[#f3f2f1] py-0.5 text-center text-[#706d71]">
                  {index + 5}
                </div>
                {report.columns.map((column) => (
                  <div key={column} className="flex-1 border-r border-stone-100 py-0.5" />
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-1 flex items-center gap-1 font-mono text-[5px] text-[#706d71]">
          <span className="rounded border border-stone-300 bg-white px-1 py-0.5 font-bold text-[#107c41]">Reporte</span>
          <span className="px-1">Listo</span>
        </div>
      </div>

      <div
        className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-white/95 transition-opacity duration-300"
        style={{
          opacity: state.generating ? 1 : 0,
          pointerEvents: 'none',
        }}
      >
        <div className="relative flex items-center justify-center">
          <svg className="vk-ring h-12 w-12">
            <circle
              className="text-stone-100"
              strokeWidth="3"
              stroke="currentColor"
              fill="transparent"
              r="20"
              cx="24"
              cy="24"
            />
            <circle
              className="text-[#107c41]"
              strokeWidth="3"
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={RING_CIRCUMFERENCE - (RING_CIRCUMFERENCE * progress) / 100}
              stroke="currentColor"
              fill="transparent"
              r="20"
              cx="24"
              cy="24"
            />
          </svg>
          <span className="absolute font-mono text-[8px] font-bold text-stone-700">{progress}%</span>
        </div>
        <p className="text-[7px] font-bold uppercase tracking-tight text-[#1c1917]">
          {GENERATION_LABELS[Math.min(2, Math.floor(progress / 34))]}
        </p>
      </div>

      <div
        className="absolute bottom-9 left-1/2 z-30 flex items-center gap-1 rounded bg-[#107c41] px-2.5 py-1 text-[7px] font-bold text-white shadow-md transition-all duration-300"
        style={{
          opacity: state.downloadToast ? 1 : 0,
          transform: `translate(-50%, ${state.downloadToast ? '0' : '0.75rem'})`,
        }}
      >
        <CheckIcon className="h-2.5 w-2.5" />
        <span>Descarga exitosa</span>
      </div>
    </JourneyView>
  )
}
